import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../App';
import { Wrench, Briefcase, Mail, Lock, User, ArrowRight, Eye, EyeOff, Shield, Star, Users, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect') || '/';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState([]);

  useEffect(() => {
    checkDemoMode();
  }, []);

  const checkDemoMode = async () => {
    try {
      const statusResponse = await axios.get(`${BACKEND_URL}/api/demo-mode/status`);
      setDemoMode(statusResponse.data.demo_mode);
      
      if (statusResponse.data.demo_mode) {
        const accountsResponse = await axios.get(`${BACKEND_URL}/api/demo-mode/accounts`);
        setDemoAccounts(accountsResponse.data.demo_accounts || []);
      }
    } catch (error) {
      console.error('Failed to check demo mode:', error);
    }
  };

  const handleDemoLogin = async (account) => {
    setLoading(true);
    const result = await login(account.email, account.password);
    
    if (result.success) {
      toast.success(`Logged in as ${account.type}!`);
      navigate(redirectPath);
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      // Check if password change is required
      if (result.must_change_password) {
        toast.info('Password change required');
        navigate('/change-password');
        setLoading(false);
        return;
      }
      
      toast.success('Welcome back!');
      setFormData({ email: '', password: '' });
      navigate(redirectPath);
    } else {
      toast.error(result.error);
      setFormData(prev => ({ ...prev, password: '' }));
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100 dark:from-gray-900 dark:via-blue-950 dark:to-gray-900">
      <div className="container mx-auto px-4 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Left Side - Branding */}
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 dark:bg-blue-900 rounded-full mb-6">
              <Wrench className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <Briefcase className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <User className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-blue-700 dark:text-blue-300 font-semibold text-sm">GoFella Platform</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
              Welcome Back to
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-600">
                GoFella
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8">
              Sign in to access GoTradies, GoJobs, and GoTalent with one account
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-muted-foreground">Find verified tradies & get quotes</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-muted-foreground">Smart way to find the job you want</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-muted-foreground">Turn your profile into opportunities</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-muted-foreground">All professionals verified</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Star className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-muted-foreground">Real reviews from real customers</span>
              </div>
            </div>

            <div className="hidden lg:block">
              <img 
                src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%230ea5e9' width='400' height='300' rx='20'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='48' fill='white' text-anchor='middle' dy='.3em'%3E🔧%3C/text%3E%3C/svg%3E"
                alt="GoFella" 
                className="w-full max-w-md mx-auto lg:mx-0 rounded-2xl shadow-2xl"
              />
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full max-w-md mx-auto lg:mx-0">
            <div className="bg-card rounded-2xl shadow-2xl p-8 md:p-10 border-2 border-border">
              <div className="text-center mb-8">
                <div className="flex justify-center gap-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-2 mb-4 shadow-lg">
                    <Wrench className="w-8 h-8 text-white" />
                  </div>
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-2 mb-4 shadow-lg">
                    <Briefcase className="w-8 h-8 text-white" />
                  </div>
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mx-2 mb-4 shadow-lg">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-foreground">Sign in to GoFella</h2>
                <p className="text-muted-foreground mt-2">Access all your services in one place</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-11 pr-4 py-3 border border-border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-background text-foreground"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-foreground">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full pl-11 pr-11 py-3 border border-border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-background text-foreground"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="text-muted-foreground"></div>
                  <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium">
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-4 rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>

              {/* Demo Quick Login (only when DEMO_MODE is ON) */}
              {demoMode && demoAccounts.length > 0 && (
                <div className="mt-6 p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                  <p className="text-sm font-semibold text-orange-800 dark:text-orange-200 mb-3">
                    🎭 Demo Mode: Quick Login
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {demoAccounts.map((account, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleDemoLogin(account)}
                        disabled={loading}
                        className="px-3 py-2 text-xs bg-white dark:bg-gray-800 border border-orange-300 dark:border-orange-700 rounded hover:bg-orange-100 dark:hover:bg-orange-900/40 disabled:opacity-50 text-foreground"
                        title={`${account.email}\n${account.description}`}
                      >
                        {account.type}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-orange-700 dark:text-orange-300 mt-2">
                    For testing only. Not visible when Demo Mode is OFF.
                  </p>
                </div>
              )}

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <Link to="/register" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 font-semibold">
                    Sign up for free
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
